import React from "react";
import { Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import PendingAffiliationsTab from "./PendingAffiliationsTab";
import ActiveAffiliationsTab from "./ActiveAffiliationsTab";
import OrganizationInfoTab from "./OrganizationInfoTab";
import NewAffiliationTab from "./NewAffiliationTab";
import ChannelsTab from "./ChannelsTab";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import SignedOutPrompt from "../SignedOutPrompt";
import AccountUnderReview from "../signup/AccountUnderReview";

/**
 * Organization admin console (affiliations, org info) — org accounts only.
 * @param {Object} props
 * @param {number} props.userId
 * @param {Object} [props.user]
 * @param {function} [props.refreshUser]
 * @returns {JSX.Element}
 */
function OrganizationPage({ userId, user, refreshUser }) {
  const location = useLocation();
  const reduceMotion = useReducedMotion();

  const tabs = [
    { path: "new", label: "New affiliation" },
    { path: "pending", label: "Pending" },
    { path: "active", label: "Active" },
    { path: "channels", label: "Channels" },
    { path: "info", label: "Organization info" },
  ];

  /**
   * @param {string} path
   * @returns {boolean}
   */
  const isActiveTab = (path) => {
    return (
      location.pathname === `/organization-admin/${path}` ||
      (location.pathname === "/organization-admin" && path === "new")
    );
  };

  if (!userId) {
    return (
      <SignedOutPrompt
        title="Organization admin"
        description="Sign in with an organization account to manage affiliations and settings."
      />
    );
  }

  if (user?.type !== "organization") {
    return (
      <div className="flex min-h-[calc(100dvh-3.5rem)] flex-col items-center justify-center bg-ui-background p-4">
        <Card className="w-full max-w-md shadow-profile-md">
          <CardHeader className="text-center">
            <CardTitle className="text-xl text-text-primary">Organization account required</CardTitle>
            <p className="pt-1 text-sm text-text-secondary">
              Sign in with an organization account to manage affiliations and settings.
            </p>
          </CardHeader>
          <CardContent className="flex justify-center pb-6">
            <Button asChild>
              <Link to="/login">Go to login</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (user?.status !== "active") {
    return <AccountUnderReview variant="organization" />;
  }

  const pageEnter =
    reduceMotion
      ? {}
      : {
          initial: { opacity: 0, y: 6 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.22, ease: [0.25, 0.46, 0.45, 0.94] },
        };

  return (
    <div className="bg-ui-background pb-8">
      <motion.div className="mx-auto max-w-7xl px-4 py-6 sm:py-8" {...pageEnter}>
        <h1 className="mb-6 text-2xl font-bold tracking-tight text-text-primary sm:mb-8 sm:text-3xl">
          Organization
        </h1>

        <div className="flex flex-col gap-4 lg:flex-row lg:gap-8">
          <aside className="w-full shrink-0 lg:w-60">
            <Card className="overflow-hidden p-1 shadow-profile-sm">
              <nav className="flex flex-col gap-0.5" aria-label="Organization admin sections">
                {tabs.map((tab) => {
                  const active = isActiveTab(tab.path);
                  return (
                    <Button
                      key={tab.path}
                      asChild
                      variant={active ? "default" : "ghost"}
                      className={cn(
                        "h-auto w-full justify-start px-3 py-2.5 text-left font-normal",
                        !active && "text-text-secondary"
                      )}
                    >
                      <Link to={`/organization-admin/${tab.path}`}>{tab.label}</Link>
                    </Button>
                  );
                })}
              </nav>
            </Card>
          </aside>

          <div className="min-w-0 flex-1">
            <Card className="min-h-[12rem] shadow-profile-sm">
              <CardContent className="p-4 sm:p-6">
                <Routes>
                  <Route path="/" element={<Navigate to="/organization-admin/new" replace />} />
                  <Route
                    path="new"
                    element={
                      <NewAffiliationTab userId={userId} user={user} refreshUser={refreshUser} />
                    }
                  />
                  <Route
                    path="pending"
                    element={
                      <PendingAffiliationsTab userId={userId} user={user} refreshUser={refreshUser} />
                    }
                  />
                  <Route
                    path="active"
                    element={
                      <ActiveAffiliationsTab userId={userId} user={user} refreshUser={refreshUser} />
                    }
                  />
                  <Route
                    path="channels"
                    element={<ChannelsTab userId={userId} user={user} />}
                  />
                  <Route
                    path="info"
                    element={
                      <OrganizationInfoTab userId={userId} user={user} refreshUser={refreshUser} />
                    }
                  />
                </Routes>
              </CardContent>
            </Card>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default OrganizationPage;
